import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material'
import { Refine } from '@refinedev/core'
import { RefineSnackbarProvider, useNotificationProvider, RefineThemes } from '@refinedev/mui'
import routerBindings from '@refinedev/react-router-v6'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import type { PropsWithChildren } from 'react'
import { BrowserRouter } from 'react-router-dom'

import { getResourcesForRefine } from '../config/resources'

import { authProvider } from './authProvider'
import { archonDataProvider as dataProvider } from './dataProvider'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const RefineApp: React.FC<PropsWithChildren> = ({ children }) => {
  const notificationProvider = useNotificationProvider()

  return (
    <ThemeProvider theme={RefineThemes.Blue}>
      <CssBaseline />
      <GlobalStyles styles={{ html: { WebkitFontSmoothing: 'auto' } }} />
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        notificationProvider={notificationProvider}
        routerProvider={routerBindings}
        resources={getResourcesForRefine()}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          projectId: 'admin-portal',
        }}
      >
        {children}
      </Refine>
    </ThemeProvider>
  )
}

export const AppProvider: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <RefineSnackbarProvider>
          <RefineApp>{children}</RefineApp>
        </RefineSnackbarProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </BrowserRouter>
  )
}
